import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Heart, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Messages() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [fetching, setFetching] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/me/messages");
      setMessages(res.data);
    } finally { setFetching(false); }
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (user) load();
  }, [loading, user, navigate, load]);

  return (
    <div className="min-h-[100dvh]">
      <Header />
      <div className="mx-auto max-w-2xl px-5 py-8 pb-28">
        <div className="mb-6 flex items-center gap-2">
          <Heart size={18} className="text-sky-400" />
          <h1 className="text-2xl font-medium">{t("messages.title")}</h1>
        </div>

        {fetching ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-sky-400" /></div>
        ) : messages.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-card/40 p-10 text-center text-sm text-muted-foreground" data-testid="messages-empty">
            {t("messages.empty")}
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => navigate(`/candle/${m.candle_id}`)}
                className="flex w-full items-start justify-between gap-3 rounded-2xl border border-white/10 bg-card/40 p-4 text-left transition-colors hover:border-white/25"
                data-testid="user-message-item"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-sm font-medium text-sky-300">{m.author_name}</span>
                    <span className="text-xs text-muted-foreground">→ {m.deceased_name || "—"}</span>
                    <span className="text-xs text-muted-foreground">· {new Date(m.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="mt-1 text-sm text-foreground/90">{m.message}</p>
                  {m.reaction_count > 0 && (
                    <p className="mt-1 text-xs text-amber-300/80">{m.reaction_count} {t("messages.reactions")}</p>
                  )}
                </div>
                <ChevronRight size={18} className="mt-1 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
